import { Form, Formik } from 'formik';
import React from 'react';
import { useQueryClient } from 'react-query';

import { PrimaryButton, SecondaryButton } from '@components/Button/variants';
import ExpiryField from '@components/Form/ExpiryField';
import FieldLabel from '@components/Form/FieldLabel';
import SwitchField from '@components/Form/SwitchField';
import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import {
  usePaymentMethodUpdate,
  useRemovePaymentMethod,
  useSetPaymentMethodToDefault,
} from '@hooks';
import useEventTracking from '@hooks/useEventTracking';
import { Theme } from '@interfaces/global';
import { AddressElement } from '@stripe/react-stripe-js';
import { useElements } from '@stripe/react-stripe-js';
import { ClickTarget, ContentType, click } from '@tracking';
import { PAYMENT_METHOD_LIST_KEY, PaymentMethod } from '@utils/backend-api/stripe';
import { CARD_PAYMENT_TYPE } from '@utils/constants';
import { getImgixSrc } from '@utils/images';

import schema from './schema';

interface EditPaymentMethodProps {
  numberOfPaymentMethods: number;
  paymentMethod: PaymentMethod;
}

const defaultAddress = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
};

const MINIMUM_PAYMENT_METHODS = 1;
const SAVE = 'Save';
const REMOVE_CARD = 'Remove Card';
const PAYMENT_METHOD_UPDATE = 'Update payment information';

const EditPaymentMethod = ({ paymentMethod, numberOfPaymentMethods }: EditPaymentMethodProps) => {
  const { id, address, card, paymentType, name, isPrimary } = paymentMethod;
  const stripeCustomer = {
    firstName: name?.split(' ')[0] || '',
    lastName: name?.split(' ')[1] || '',
  };
  const { trackEvent } = useEventTracking({
    contentId: id,
    contentType: ContentType.PaymentMethod,
  });
  const isCardPaymentType = paymentType === CARD_PAYMENT_TYPE;
  const elements = useElements();
  const { mutateAsync: removePaymentMethod, isLoading: isRemovingPaymentMethod } =
    useRemovePaymentMethod();
  const { mutateAsync: setPaymentMethodToDefault, isLoading: isSettingPaymentToDefault } =
    useSetPaymentMethodToDefault();
  const { mutateAsync: updatePaymentMethod, isLoading: isUpdatePaymentMethod } =
    usePaymentMethodUpdate();
  const queryClient = useQueryClient();

  const handleSetPaymentMethodToDefault = async ({ isPrimary }) => {
    await setPaymentMethodToDefault({ payment_method_id: id, is_primary: isPrimary });
    queryClient.invalidateQueries(PAYMENT_METHOD_LIST_KEY);
  };

  const handleRemovePaymentPayment = async () => {
    await removePaymentMethod({ payment_method_id: id });
    queryClient.invalidateQueries(PAYMENT_METHOD_LIST_KEY);
  };

  const handleUpdatePaymentMethod = async ({ cardExpiration }) => {
    const [expMonth, expYearShort] = cardExpiration.split('/');
    const expMonthNum = parseInt(expMonth, 10);
    const expYearNum = 2000 + parseInt(expYearShort, 10);
    const { value: customerDetails } = await elements.getElement(AddressElement).getValue();

    await updatePaymentMethod({
      payment_method_id: id,
      exp_month: expMonthNum,
      exp_year: expYearNum,
      address: customerDetails.address || defaultAddress,
      name: customerDetails.name,
    });
    queryClient.invalidateQueries(PAYMENT_METHOD_LIST_KEY);
  };

  return (
    <div className="flex flex-col items-center justify-center pt-48">
      {!isPrimary && (
        <Formik
          enableReinitialize
          initialValues={{ isPrimary: isPrimary }}
          onSubmit={handleSetPaymentMethodToDefault}
        >
          <Form className="flex w-full flex-col gap-24 pb-32 sm:w-[440px]" noValidate>
            <div className="text-left font-suisse-intl text-body-lg font-light">
              Setup card as your Primary payment method
            </div>
            <SwitchField colorTheme={Theme.Light} label="Primary payment method" name="isPrimary" />
            <PrimaryButton
              as="button"
              size="big"
              className="w-full"
              onClickCapture={() =>
                trackEvent(click({ clickTarget: ClickTarget.SetAsPrimary, clickText: SAVE }))
              }
            >
              {isSettingPaymentToDefault ? (
                <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} />
              ) : (
                <>{SAVE}</>
              )}
            </PrimaryButton>
          </Form>
        </Formik>
      )}
      {isCardPaymentType && (
        <Formik
          enableReinitialize
          initialValues={{ cardExpiration: '' }}
          onSubmit={handleUpdatePaymentMethod}
          validationSchema={schema}
        >
          {(formik) => (
            <Form className="w-full sm:w-[440px]">
              <div className="pb-16 text-left font-suisse-intl text-body-lg font-light">
                Update payment information
              </div>
              <div id="address-form">
                <AddressElement
                  options={{
                    mode: 'billing',
                    display: {
                      name: 'split',
                    },
                    defaultValues: {
                      address: address || defaultAddress,
                      firstName: stripeCustomer.firstName,
                      lastName: stripeCustomer.lastName,
                    },
                  }}
                />
              </div>
              <div className="flex justify-between pt-32">
                <div className="flex w-full gap-16">
                  <div className="flex min-w-[216px] flex-col">
                    <FieldLabel htmlFor="" className="text-left">
                      Card Number
                    </FieldLabel>
                    <div className="flex gap-16">
                      <img
                        src={getImgixSrc({
                          path: `update_payment/${card.brand}.svg`,
                          isEnvDependent: false,
                        })}
                        alt={card.brand}
                        className="w-32 self-start"
                      />
                      <div className="font-suisse-intl text-description-lg text-grayscale-gray">
                        **** **** **** {card.last4}
                      </div>
                    </div>
                  </div>
                  <div>
                    <FieldLabel htmlFor="" className="text-left">
                      CVC
                    </FieldLabel>
                    <div className="font-suisse-intl text-description-lg text-grayscale-gray">
                      ****
                    </div>
                  </div>
                </div>
              </div>
              <ExpiryField
                name="cardExpiration"
                label="Expiration"
                className="max-w-[216px] py-24 text-left font-suisse-intl text-description-sm"
              />
              <div className="pb-24 text-left font-suisse-screen text-disclosure-sm text-grayscale-gray">
                By providing your card information, you allow The Information to charge your card
                for future payments in accordance with their terms.
              </div>
              <PrimaryButton
                as="button"
                size="big"
                className="w-full"
                disabled={!formik.dirty || !formik.isValid}
                onClickCapture={() =>
                  trackEvent(
                    click({
                      clickTarget: ClickTarget.UpdatePaymentInformation,
                      clickText: PAYMENT_METHOD_UPDATE,
                    })
                  )
                }
              >
                {isUpdatePaymentMethod ? (
                  <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} />
                ) : (
                  <>{PAYMENT_METHOD_UPDATE}</>
                )}
              </PrimaryButton>
            </Form>
          )}
        </Formik>
      )}

      {numberOfPaymentMethods > MINIMUM_PAYMENT_METHODS && !isPrimary && (
        <SecondaryButton
          as="button"
          size="big"
          className="mt-16 w-full sm:max-w-[440px]"
          onClick={handleRemovePaymentPayment}
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.RemoveCard, clickText: REMOVE_CARD }))
          }
        >
          {isRemovingPaymentMethod ? (
            <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} />
          ) : (
            <>{REMOVE_CARD}</>
          )}
        </SecondaryButton>
      )}
    </div>
  );
};

export default EditPaymentMethod;
